import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { TenantDto, Theme } from '@bo-client/shared/models/tenantDto';
import { UserDto } from '@bo-client/shared/models/userDto';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { uri } from '@bo-client/shared/constants/uri';
import { UserBackOfficeSettingsModel } from '@bo-client/shared/models/userBackOfficeSettings';
import { ColumnEditorModel } from '@bo-client/shared/dynamic-search-serverside/dynamic-search.model';
import { UiAdaptionsModel } from '@bo-client/tenant-management/models/ui-adaptions.model';
import { NBPConfig, StyleConfig } from '@bo-client/shared/config.model';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { CompanyLocationDto } from '../models/companyLocations';
import { ObjectPath } from '@bo-client/shared/types/ObjectPath.type';

@Injectable({
  providedIn: 'root'
})
export class DataShareService {
  public currentTenant$: Observable<TenantDto>;
  public uiAdaptions$: Observable<Array<UiAdaptionsModel>>;
  public userConfiguration$: Observable<UserBackOfficeSettingsModel>;
  public tenantConfig$: Observable<NBPConfig>;
  public availableTenants$: Observable<Array<TenantDto>>;
  public userData$: Observable<UserDto>;
  public theme$: Observable<Theme>;
  public pubSubHelper$: Observable<any>;
  public isLoading$: Observable<boolean>;
  public isIAMUser$: Observable<boolean>;
  public companyLocations$: Observable<CompanyLocationDto[]>;
  private _tenantConfig$: BehaviorSubject<NBPConfig> = new BehaviorSubject({});
  private _tenantSource$ = new BehaviorSubject(null);
  private _uiAdaptionsSource$ = new BehaviorSubject<UiAdaptionsModel[]>([]);
  private _userConfiguration$ = new BehaviorSubject<UserBackOfficeSettingsModel>(
    new UserBackOfficeSettingsModel()
  );
  private _availableTenants$: BehaviorSubject<Array<TenantDto>> = new BehaviorSubject<Array<TenantDto>>([]);
  private _userData$: BehaviorSubject<UserDto> = new BehaviorSubject<UserDto>(null);

  private _theme$: BehaviorSubject<Theme> = new BehaviorSubject<Theme>(null);
  // TODO Refactor anything related to this..
  private _pubSubHelper$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private _isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private httpHapService: HttpHapService) {
    this.currentTenant$ = this._tenantSource$.asObservable();
    this.uiAdaptions$ = this._uiAdaptionsSource$.asObservable();
    this.userConfiguration$ = this._userConfiguration$.asObservable();
    this.userData$ = this._userData$.asObservable();
    this.availableTenants$ = this._availableTenants$.asObservable();
    this.theme$ = this._theme$.asObservable();
    this.isLoading$ = this._isLoading$.asObservable();
    this.pubSubHelper$ = this._pubSubHelper$.asObservable();
    this.tenantConfig$ = this._tenantConfig$.asObservable();

    this.isIAMUser$ = this.userData$.pipe(
      map((x) => {
        return !!x && !!x.idpIdentifier;
      })
    );

    this.companyLocations$ = this._tenantSource$.pipe(
      switchMap((tenant) => {
        return this.httpHapService.get(uri.companyLocationByCurrentTenant);
      })
    );
  }

  get availableTenants(): TenantDto[] {
    return this._availableTenants$.getValue();
  }

  get tenantConfig(): NBPConfig {
    return this._tenantConfig$.getValue();
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSingleTenantConfig$<T = any>(path: ObjectPath<NBPConfig> | string, fallbackValue?: T): Observable<T> {
    return this.tenantConfig$.pipe(
      filter((config) => !!config && !!Object.keys(config).length),
      map((config) => {
        if (!path) return fallbackValue || undefined;
        const value = path.split('.').reduce((acc, key) => {
          return acc?.[key];
        }, config) as T | undefined;
        if ((value === undefined || value === null) && fallbackValue !== undefined) {
          return fallbackValue;
        }
        return value;
      }),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      shareReplay(1)
    );
  }

  get uiAdaptions(): Array<UiAdaptionsModel> {
    return this._uiAdaptionsSource$.getValue();
  }

  get currentUserConfiguration(): UserBackOfficeSettingsModel {
    return this._userConfiguration$.getValue();
  }

  get currentUser(): UserDto {
    return this._userData$.getValue();
  }

  get currentTenant(): TenantDto {
    return this._tenantSource$.getValue();
  }

  get selectedTenant(): TenantDto {
    return this._tenantSource$.getValue();
  }

  logout() {
    this._userConfiguration$.next(new UserBackOfficeSettingsModel());
    this._userData$.next(null);
    this._availableTenants$.next([]);
    console.log('last thing in logout wiipue');
  }

  setTenant(currentTenant: TenantDto) {
    console.log('changing tenant: ', currentTenant);

    this._tenantSource$.next(currentTenant);
    this._theme$.next(currentTenant.theme);
    this.saveSelectedTenant(currentTenant);
    this.refreshTenantRelatedConfiguration(currentTenant);
  }

  setIsLoading(isLoading: boolean) {
    this._isLoading$.next(isLoading);
  }

  setUserData(userDto: UserDto) {
    this._availableTenants$.next(userDto.allowedTenants);
    let tenantToSelect = userDto.allowedTenants[0];
    this.setTenant(tenantToSelect);

    if (userDto.allowedTenants.length > 0 && userDto.backofficeSettings) {
      let newConfig: any = {};
      try {
        newConfig = JSON.parse(userDto.backofficeSettings);
        if (!newConfig.listConfigurations) {
          newConfig.listConfigurations = {};
        }
        if (!newConfig.pageConfigs) {
          newConfig.pageConfigs = {};
        }
        tenantToSelect = userDto.allowedTenants.find((tenant) => tenant.id === newConfig.selectedTenant.id);
      } catch (e) {
        newConfig.listConfigurations = {};
        newConfig.pageConfigs = {};
      }
      this._userConfiguration$.next(newConfig);
    }

    this._userData$.next(userDto);
  }

  saveOrderRequestConfiguration(columns: ColumnEditorModel[]) {
    this.saveListConfiguration('OrderRequest', columns);
  }

  saveInstallerPassConfiguration(columns: ColumnEditorModel[]) {
    this.saveListConfiguration('InstallerPass', columns);
  }

  saveCommissioningRequestConfiguration(columns: ColumnEditorModel[]) {
    this.saveListConfiguration('CommissioningRequest', columns);
  }

  saveBusinessPartnerConfiguration(columns: ColumnEditorModel[]) {
    this.saveListConfiguration('BusinessPartner', columns);
  }

  saveGridRegistrationConfiguration(columns: ColumnEditorModel[]) {
    this.saveListConfiguration('GridRegistration', columns);
  }

  saveListConfiguration(prefix: string, columns: ColumnEditorModel[]) {
    const newConfiguration = this.currentUserConfiguration;
    newConfiguration.listConfigurations[prefix] = columns;

    this.httpHapService.update(newConfiguration, uri.updateBackofficeSettings).subscribe((success) => {
      this._userConfiguration$.next(newConfiguration);
    });
  }

  resetConfiguration() {
    const newConfiguration = this.currentUserConfiguration;
    newConfiguration.listConfigurations = {};
    newConfiguration.pageConfigs = {};
    this.httpHapService.update({}, uri.updateBackofficeSettings).subscribe((success) => {
      this._userConfiguration$.next(newConfiguration);
    });
  }

  savePageConfig(prefix: string, pageConfig: any) {
    const newConfiguration = this.currentUserConfiguration;
    newConfiguration.pageConfigs[prefix] = pageConfig;

    this.httpHapService.update(newConfiguration, uri.updateBackofficeSettings).subscribe((success) => {
      this._userConfiguration$.next(newConfiguration);
    });
  }

  saveSelectedTenant(tenant: TenantDto) {
    if (this.currentUser) {
      const newConfiguration = this.currentUserConfiguration;
      newConfiguration.selectedTenant = tenant;
      // Commented to avoid the changeTenant functionality valid only for Super-Admin
      // this.httpHapService.update(JSON.stringify(newConfiguration), uri.updateBackofficeSettings).subscribe(success => {
      //   this._userConfiguration$.next(newConfiguration);
      // });
    }
  }

  changeData(data: any) {
    this._pubSubHelper$.next(data);
  }

  /**
   * Speichert das UI-Adaption-Modell in den UI-Adaptionen und aktualisiert die UI-Adaptionen-Liste
   * @param model Das zu speichernde UI-Adaption-Modell
   * @returns Ein Observable, das das gespeicherte UI-Adaption-Modell zurückgibt
   */
  public putOrPostUiAdaption(model: UiAdaptionsModel) {
    const adaption$ = this.httpHapService.putOrPost(model, uri.uiAdaptions).pipe(
      map((response: UiAdaptionsModel) => response),
      shareReplay(1) // wird benötigt, um das Observable mehrmals zu verwenden und eine weitere Post-Anfrage zu vermeiden
    ) as Observable<UiAdaptionsModel>;
    adaption$.subscribe((adaption) => {
      const adaptions = this.uiAdaptions;
      const index = adaptions.findIndex((x) => x.anchor === adaption.anchor);
      if (index >= 0) {
        adaptions[index] = adaption;
      } else {
        adaptions.push(adaption);
      }
      this._uiAdaptionsSource$.next(adaptions);
      this.updateCSSVariablesByAdaptions(adaptions, this.tenantConfig.style);
    });
    return adaption$;
  }

  /**
   * Löscht die UI-Adaption mit der angegebenen ID
   * @param adaptionId Die ID der zu löschenden UI-Adaption
   * @returns Ein Observable, das bei Erfolg void zurückgibt
   */
  public deleteUiAdaption(adaptionId: number | string) {
    const adaption$ = this.httpHapService.delete(uri.uiAdaptions + '/' + adaptionId).pipe(
      shareReplay(1) // wird benötigt, um das Observable mehrmals zu verwenden und eine weitere Delete-Anfrage zu vermeiden
    ) as Observable<void>;
    adaption$.subscribe(() => {
      const adaptions = this.uiAdaptions;
      const index = adaptions.findIndex((adaption) => adaption.id.toString() === adaptionId.toString());
      if (index >= 0) {
        adaptions.splice(index, 1);
      }
      this._uiAdaptionsSource$.next(adaptions);
      this.updateCSSVariablesByAdaptions(adaptions, this.tenantConfig.style);
    });
    return adaption$;
  }

  private refreshTenantRelatedConfiguration(tenant: TenantDto) {
    // Erstellen Sie Observables, aber abonnieren Sie sie noch nicht
    const tenantConfig$ = this.httpHapService.getTyped<NBPConfig>(`/config/${tenant.id}/master`);
    const uiAdaptions$ = this.httpHapService.get(uri.uiAdaptionsByTenant);

    // Benutzen Sie forkJoin, um beide Anfragen parallel auszuführen
    forkJoin([tenantConfig$, uiAdaptions$])
      .pipe(
        tap(([config, adaptions]) => {
          // Verarbeiten Sie die Antworten nachdem beide Anfragen abgeschlossen sind
          this._tenantConfig$.next(config);
          this._uiAdaptionsSource$.next(adaptions);
          this.updateCSSVariablesByAdaptions(adaptions, config.style);
        })
      )
      .subscribe(); // Abonnieren Sie das Ergebnis-Observable, um die Anfragen auszuführen
  }

  updateNBPConfig(tenantId: string) {
    const tenantConfig$ = this.httpHapService.getTyped<NBPConfig>(`/config/${tenantId}/master`);
    tenantConfig$.subscribe((value) => {
      this._tenantConfig$.next(value);
    });
  }

  private updateCSSVariablesByAdaptions(adaptions: any, style: StyleConfig) {
    // first apply config
    if (style && style.cssVariables) {
      Object.keys(style.cssVariables).forEach((x) => {
        // by convention we only have variable names without underscore
        document.documentElement.style.setProperty('--' + 'theme-' + x, style.cssVariables[x]);
      });
    }

    // if there is something in the ui adaptions.. use that instead for downwardscompatibility
    const css_variables = adaptions.filter((x) => x.anchor.startsWith('css_variable_'));
    if (css_variables) {
      css_variables.forEach((x) => {
        // by convention we only have variable names without underscore
        const variableName = x.anchor.split('_').pop();
        const variableValue = x.value;

        document.documentElement.style.setProperty('--' + variableName, variableValue);
      });
    }

    // Seitentitel
    const titleAdapt = adaptions.find((x) => x.anchor === 'page_title');
    if (titleAdapt) {
      document.title = titleAdapt.value; // z. B. "Dashboard – Acme Inc."
    }
  }
}
